import { CheckoutContext } from '@/context/CheckoutContext';
import { generateZendeskSupportURL } from '@/utils/support';
import { useContext } from 'react';
import { useTranslate } from '@/hooks/useTranslate';
import { useGpcMarketingEmailOptOut } from '@/hooks/useGpcMarketingEmailOptOut';
import { getFooterLinks, getFooterSupportLabel } from '@/constants/footerLinks';
import { getCustomerCountry } from '@/utils/customer';
import { Divider } from '../Common/Divider';
import { PoweredByAppcharge } from './PoweredByAppcharge';
import { ApprovalText } from '../PaymentInputs/ApprovalText';
import { SubscriptionAgreement } from '../Subscriptions/SubscriptionAgreement/SubscriptionAgreement';
import { DoNotSellModal } from './OptOutModal/DoNotSellModal';

export const Footer = () => {
  const { customer, publisherId, checkoutConfiguration } = useContext(CheckoutContext);
  const { t } = useTranslate();

  const marketingEmailOptOutEnabled = Boolean(checkoutConfiguration?.showMarketingEmailOptOut);
  const {
    hasOptedOut,
    isDoNotSellOpen,
    openDoNotSellModal,
    closeDoNotSellModal,
    doNotSellHasResult,
    doNotSellIsSuccess,
    doNotSellIsPending,
    submitDoNotSellOptOut,
  } = useGpcMarketingEmailOptOut(marketingEmailOptOutEnabled);
  const showDoNotSell = marketingEmailOptOutEnabled && !hasOptedOut;

  const customerCountry = getCustomerCountry(customer);
  const legalLinks = getFooterLinks(customerCountry);
  const supportLabel = getFooterSupportLabel(customerCountry);

  return (
    <div className="w-full">
      <Divider />
      {/* Footer block: Appcharge branding + security/legal links sit first
          (below the divider); the legal/EULA disclaimer ("By clicking Pay…")
          sits LAST, under the legal-links row (matches Figma). The divider
          stays full-width while the content is inset with horizontal padding
          so the legal paragraph wraps in a comfortable column. */}
      <div className="w-full flex flex-col items-center justify-center gap-2 px-4 py-4">
        <PoweredByAppcharge />
        <div className="flex flex-col items-center justify-center gap-2">
          {/* Support always renders last among external links (its href is
              per-customer, so it sits outside the country-scoped list), which
              lets every legal link above it carry a trailing separator. When
              Do Not Sell is shown it becomes the true last item (ACDEV-29991). */}
          <div className="flex flex-wrap items-center justify-center gap-2 text-[10px] leading-4 text-muted-foreground">
            {legalLinks.map(({ labelKey, url, label }) => (
              <a key={labelKey} href={url} target="_blank" rel="noopener noreferrer">
                {label ?? t(labelKey)} |
              </a>
            ))}
            <a
              href={generateZendeskSupportURL({
                customerId: customer?.id,
                orderId: '',
                publisherId,
                customerCountryCode: customer?.geoLocator?.countryCode2,
              })}
              target="_blank"
              rel="noopener noreferrer"
            >
              {supportLabel ?? t('support')}
              {showDoNotSell ? ' |' : ''}
            </a>
            {showDoNotSell && (
              <button
                type="button"
                data-testid="do-not-sell-link"
                className="cursor-pointer bg-transparent p-0 text-[10px] leading-4 text-muted-foreground"
                onClick={openDoNotSellModal}
              >
                {t('doNotSell')}
              </button>
            )}
          </div>
        </div>
        <ApprovalText withPadding={false} />
        <SubscriptionAgreement />
      </div>
      {isDoNotSellOpen && (
        <DoNotSellModal
          onClose={closeDoNotSellModal}
          onOptOut={submitDoNotSellOptOut}
          hasResult={doNotSellHasResult}
          isSuccess={doNotSellIsSuccess}
          isPending={doNotSellIsPending}
        />
      )}
    </div>
  );
};
